'use client';

import { useEffect } from 'react';

import {
  deriveBorderColor,
  deriveMutedColor,
  deriveMutedForeground,
} from '~/lib/utils/color-derivation';

interface BodyBrandingStyleProps {
  primaryColor: string;
  secondaryColor: string;
  appBackgroundColor?: string;
  appTextColor?: string;
  /**
   * Admin / staff surfaces. When set, the tenant colour informs `--tenant-brand`
   * (previews / AI launcher) but does NOT drive `--primary` — the primary ACTION
   * colour is Askflow ink, applied via the `ask-brand-neutral` class (mirrored
   * onto <body> here so portalled Dialog/Popover/Menu buttons match). Omit on
   * customer-facing surfaces, which paint `--primary` from the tenant colour.
   */
  staffFacing?: boolean;
}

/**
 * Injects tenant branding CSS variables into document.body.
 * This ensures portal-based components (Sheet, Dialog, Popover) inherit theme colors,
 * since Radix UI portals render outside the main app wrapper div that contains
 * the inline branding styles.
 */
export function BodyBrandingStyle({
  primaryColor,
  secondaryColor,
  appBackgroundColor,
  appTextColor,
  staffFacing = false,
}: BodyBrandingStyleProps) {
  useEffect(() => {
    // Staff surfaces route the tenant colour to --tenant-brand (never --primary,
    // which the ask-brand-neutral class sets to ink); customer surfaces paint
    // --primary directly.
    const primaryVars: [string, string | undefined][] = staffFacing
      ? [
          ['--tenant-brand', primaryColor],
          ['--color-tenant-brand', primaryColor],
        ]
      : [
          ['--primary', primaryColor],
          ['--color-primary', primaryColor],
        ];

    const props: [string, string | undefined][] = [
      ...primaryVars,
      // See get-branding-style.ts: brand secondary must not clobber
      // shadcn's --secondary surface role.
      ['--brand-secondary', secondaryColor],
      ['--color-brand-secondary', secondaryColor],
      ['--background', appBackgroundColor],
      ['--card', appBackgroundColor],
      ['--foreground', appTextColor],
      ['--card-foreground', appTextColor],
    ];

    if (appBackgroundColor) {
      props.push(
        ['--muted', deriveMutedColor(appBackgroundColor)],
        ['--border', deriveBorderColor(appBackgroundColor)],
      );
    }

    if (appTextColor) {
      props.push([
        '--muted-foreground',
        deriveMutedForeground(appTextColor, appBackgroundColor),
      ]);
    }

    // Portalled overlays (Dialog/Popover/Menu) render on <body>, outside the
    // admin root's ask-brand-neutral scope — mirror the class here so their
    // primary buttons are ink too.
    if (staffFacing) {
      document.body.classList.add('ask-brand-neutral');
    }

    for (const [key, value] of props) {
      if (value) {
        document.body.style.setProperty(key, value);
      }
    }

    return () => {
      for (const [key] of props) {
        document.body.style.removeProperty(key);
      }
      if (staffFacing) {
        document.body.classList.remove('ask-brand-neutral');
      }
    };
  }, [
    primaryColor,
    secondaryColor,
    appBackgroundColor,
    appTextColor,
    staffFacing,
  ]);

  return null;
}
